// Design-time preview of the site-wide header / footer inherited by an
// external page.
//
// An External Website (X) node names a shared `header_page_obj_name` /
// `footer_page_obj_name` Smart Page that the runtime wraps around every page
// of the site. Those chrome pages live on their OWN pages, so the designer
// canvas normally showed nothing where they'll appear — authors only saw the
// header/footer after deploy/preview. This band renders the resolved chrome
// page READ-ONLY (pointer-events disabled, an "Inherited site header/footer"
// chip) above/below the page's own sections, mirroring the SSR ChromePage +
// in-app WebsiteViewerPage.renderChrome, so the canvas matches what ships.
//
// It honors the per-page override on `page.navigation`:
//   showHeader/showFooter === false   → the band shows a faint "hidden on
//                                        this page" hint instead of chrome.
//   headerPageObjName/footerPageObjName → previews that page instead of the
//                                        site default.

import { useEffect, useState } from 'react'
import type { PageModel } from '@twasta/page-schema'
import { loadPageObject } from '@/api/designers'
import { getJsonRaw } from '@/api/generateapp'
import { resolveTheme, themeCssVars, chromeBackgroundCss, contentWidthPx } from './theme'
import { SiteMenuContext, SiteMenusContext, type SiteMenuItem, type SiteMenus } from './renderers'
import { buildSiteMenu, buildSiteMenus } from './siteMenu'
import SmartPageRuntime from '@/pages/workbench/smartpage-preview/SmartPageRuntime'

interface NavItem {
  id?: string
  obj_name?: string
  obj_type?: string
  parent_id?: string
  functional_specs?: string
}

function parseSpec(fs?: string): Record<string, unknown> {
  if (!fs) return {}
  try {
    const o = JSON.parse(fs)
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

// Resolve the External Website (X) node that owns this page — the nearest X
// ancestor by parent_id — else the primary site, else the first site.
function resolveSiteSpec(nav: NavItem[], pageObjName: string): Record<string, unknown> | null {
  const byId = new Map<string, NavItem>()
  for (const n of nav) if (n.id) byId.set(n.id, n)
  let cur = nav.find((n) => n.obj_name === pageObjName)
  const seen = new Set<string>()
  while (cur && cur.parent_id && !seen.has(cur.parent_id)) {
    seen.add(cur.parent_id)
    const parent = byId.get(cur.parent_id)
    if (!parent) break
    if (parent.obj_type === 'X') return parseSpec(parent.functional_specs)
    cur = parent
  }
  const xs = nav.filter((n) => n.obj_type === 'X')
  const primary = xs.find((n) => parseSpec(n.functional_specs).is_primary) || xs[0]
  return primary ? parseSpec(primary.functional_specs) : null
}

export function InheritedChromeBand({
  projectName, page, slot,
}: {
  projectName: string
  page: PageModel
  slot: 'header' | 'footer'
}) {
  const nav = page.navigation || {}
  const disabled = slot === 'header' ? nav.showHeader === false : nav.showFooter === false
  const overrideName = slot === 'header' ? nav.headerPageObjName : nav.footerPageObjName
  const [model, setModel] = useState<PageModel | null>(null)
  const [sourceObj, setSourceObj] = useState<string | null>(null)
  const [menu, setMenu] = useState<SiteMenuItem[]>([])
  const [menus, setMenus] = useState<SiteMenus>({})

  useEffect(() => {
    if (page.kind !== 'external' || disabled) {
      setModel(null); setSourceObj(null); return
    }
    let cancelled = false
    void (async () => {
      try {
        const res = await getJsonRaw(projectName) as { navigation?: NavItem[] }
        const navList = res.navigation || []
        // Build the site menu so a nav_menu block in the chrome previews the
        // real navigation (folders → dropdown submenus).
        if (!cancelled) {
          setMenu(buildSiteMenu(navList, page.obj_name))
          setMenus(buildSiteMenus(navList, page.obj_name))
        }
        let objName = overrideName || ''
        if (!objName) {
          const spec = resolveSiteSpec(navList, page.obj_name)
          const key = slot === 'header' ? 'header_page_obj_name' : 'footer_page_obj_name'
          objName = (spec && typeof spec[key] === 'string' ? (spec[key] as string) : '') || ''
        }
        // Don't preview a page as its own chrome (avoids self-recursion when
        // editing the header/footer page itself).
        if (!objName || objName === page.obj_name) {
          if (!cancelled) { setModel(null); setSourceObj(null) }
          return
        }
        const data = await loadPageObject(projectName, objName)
        const root = data.content as { page?: PageModel } & PageModel
        const m = (root?.page ?? root) as PageModel
        if (!cancelled) { setModel(m || null); setSourceObj(objName) }
      } catch {
        if (!cancelled) { setModel(null); setSourceObj(null) }
      }
    })()
    return () => { cancelled = true }
  }, [projectName, page.obj_name, page.kind, slot, disabled, overrideName])

  // Only external pages carry site chrome.
  if (page.kind !== 'external') return null

  if (disabled) {
    return (
      <div style={{
        margin: '4px auto', maxWidth: 720, textAlign: 'center',
        fontSize: 13, color: 'var(--pg-text-secondary, #94a3b8)',
        border: '1px dashed var(--pg-border, #e5e7eb)', borderRadius: 6,
        padding: '6px 10px', background: 'transparent',
      }}>
        Site {slot} hidden on this page (Navigation → Show site-wide {slot})
      </div>
    )
  }

  if (!model) return null

  const theme = resolveTheme(model.theme)
  const Tag = slot
  const innerMax = theme.layout.maxWidth || contentWidthPx(theme.layout)
  return (
    <div
      style={{ position: 'relative' }}
      title={`Inherited site ${slot}${sourceObj ? ` — edit it on the “${sourceObj}” page` : ''}`}
    >
      <div style={{
        position: 'absolute', top: 6, left: 8, zIndex: 2,
        fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
        color: '#fff', background: 'rgba(79,70,229,.85)', padding: '2px 8px',
        borderRadius: 999, pointerEvents: 'none',
      }}>
        Inherited site {slot}
      </div>
      {/* Read-only: the chrome is authored on its own page, not here. */}
      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {/* Use the chrome-aware background (palette color, ignoring a default
            solid-white layout background that would otherwise clobber a dark
            header/footer and wash out its light text) — parity with the
            WebsiteViewerPage preview + SSR ChromePage. */}
        <Tag style={{
          width: '100%',
          ...(themeCssVars(theme) as React.CSSProperties),
          ...(chromeBackgroundCss(theme) as React.CSSProperties),
        }}>
          <div style={{ maxWidth: innerMax, margin: '0 auto', padding: `0 ${theme.layout.paddingX}px` }}>
            <SiteMenuContext.Provider value={menu}>
            <SiteMenusContext.Provider value={menus}>
              <SmartPageRuntime page={model} mode="preview" docId={null} project={projectName} />
            </SiteMenusContext.Provider>
            </SiteMenuContext.Provider>
          </div>
        </Tag>
      </div>
    </div>
  )
}
